import React from 'react';
import { useTranslation } from 'react-i18next';
import NumberFlowReact, { useCanAnimate } from '@number-flow/react';

import { LineChart, Skeleton } from '../../../../components';
import { PULSE } from '../../../../theme/tokens';
import { PulseBadge } from '../PulseBadge';
import { BusinessValueWaves } from './BusinessValueWaves';
import { Root, TextCol, Title, Big, BadgeRow, Sub, Chart } from './BusinessValue.styles';

// Full-precision money formatter for the hover readout — "$847,250", matching the
// hero figure. `showY` is off so it only shapes the tooltip's monthly $ value.
const moneyFmt = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;

// The chart's plotted height. Grown ~30% (128 → 166) with the taller "king of
// stage" card so the chart scales up to anchor it (see `Root` min-height in the
// styles).
const CHART_HEIGHT = 166;

/**
 * The hero figure — odometer-rolls from 0 to `value` on reveal via NumberFlow (the
 * same animation the Stats / Home dashboards use), then tweens on later updates.
 * Private + mounted only once the data is in, so the roll-up plays on reveal rather
 * than under the skeleton. NumberFlow only animates on a value *change*, so the
 * entrance bumps the displayed value from 0 → target after mount; under reduced
 * motion it starts at the target (no roll, no flash of 0).
 */
const Figure = ({ value }: { value: number }): React.ReactElement => {
  const canAnimate = useCanAnimate({ respectMotionPreference: true });
  const [display, setDisplay] = React.useState(() => (canAnimate ? 0 : value));
  React.useEffect(() => {
    setDisplay(value);
  }, [value]);

  return (
    <Big>
      <NumberFlowReact value={display} prefix="$" format={{ maximumFractionDigits: 0 }} locales="en-US" animated={canAnimate} />
    </Big>
  );
};

export type BusinessValueProps = {
  value: number;
  /** Month-over-month change pill copy; empty hides the badge. */
  delta: string;
  /** Render the delta pill as the down (red) variant. */
  deltaDown?: boolean;
  sub: string;
  series: number[];
  /** Month label per `series` point — the chart tooltip's monthly heading. */
  months: string[];
  /** While true, the figure + badge + chart show placeholders (the endpoint is loading). */
  loading?: boolean;
};

/**
 * "Business Value" hero — the headline figure counts up on mount beside a wide
 * gradient area chart (the Figma violet line). The series colour is the
 * `PULSE.chartLine` token; the figure is a money value so it renders as a plain
 * node, not `<Text>`. Hovering the line reveals each month's $ total: `months`
 * feed the tooltip heading (via `pointLabels`) while the visible axis stays off.
 * The title + caption are static chrome; the figure, badge and chart load in.
 */
export const BusinessValue = ({ value, delta, deltaDown = false, sub, series, months, loading = false }: BusinessValueProps): React.ReactElement => {
  const { t } = useTranslation();
  const chartSeries = React.useMemo(
    () => [{ name: 'Business Value', color: PULSE.chartLine, points: series }],
    [series],
  );

  // `sub` is the metric's static formula caption (closed UI vocabulary), so it is
  // translated at the render site keyed off its stable English source; any other
  // value (e.g. once the API supplies free copy) passes through untranslated.
  const SUB_LABELS: Record<string, string> = {
    'Valuation Revenue x Network Multiple': t('Valuation Revenue x Network Multiple'),
  };
  const subLabel = SUB_LABELS[sub] ?? sub;

  return (
    <Root>
      <BusinessValueWaves />
      <TextCol>
        <Title text="Business Value" tag="h3" />
        {loading ? (
          // Wrapped in `Big` so the placeholder inherits the hero font-size (its
          // `em` height/width then track the real figure, desktop and mobile).
          <Big>
            <Skeleton width={`${Math.max(4, moneyFmt(value).length)}ch`} height="0.78em" radius={8} />
          </Big>
        ) : (
          <Figure value={value} />
        )}
        <BadgeRow>
          {loading ? <Skeleton width={70} height={22} radius={8} /> : delta ? <PulseBadge text={delta} down={deltaDown} /> : null}
        </BadgeRow>
        <Sub text={subLabel} tag="span" />
      </TextCol>
      <Chart>
        {/* No skeleton here: the chart owns its reveal via the `animate` entrance
            trace, so it stays empty until the data lands and then draws itself in
            (a placeholder would only double up on, and muddy, that motion). A trend
            needs at least two months — fewer (a brand under two complete months old)
            draws no line, which also avoids the empty-series area-path edge case. */}
        {loading || series.length < 2 ? null : (
          <LineChart
            series={chartSeries}
            xLabels={[]}
            pointLabels={months}
            formatY={moneyFmt}
            height={CHART_HEIGHT}
            showY={false}
            area
            endDot
            animate
          />
        )}
      </Chart>
    </Root>
  );
};

export default BusinessValue;
